/**
 * The device's durable store (OFF-001: a queue that survives reload and crash). IndexedDB in the browser, a
 * Map in tests. Values are opaque here — encryption happens above this line (vault.ts), so nothing readable
 * is ever handed to this layer.
 */
export interface KvStore {
  get(key: string): Promise<unknown>;
  put(key: string, value: unknown): Promise<void>;
  remove(key: string): Promise<void>;
  keys(prefix: string): Promise<string[]>;
}

const STORE = "kv";

const wrap = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

export function idbStore(dbName = "ebr-offline", factory: IDBFactory = indexedDB): KvStore {
  const opened = new Promise<IDBDatabase>((resolve, reject) => {
    const req = factory.open(dbName, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  const os = async (mode: IDBTransactionMode) => (await opened).transaction(STORE, mode).objectStore(STORE);
  return {
    get: async (k) => wrap((await os("readonly")).get(k)),
    put: async (k, v) => void (await wrap((await os("readwrite")).put(v, k))),
    remove: async (k) => void (await wrap((await os("readwrite")).delete(k))),
    keys: async (prefix) =>
      ((await wrap((await os("readonly")).getAllKeys())) as string[]).filter((k) => k.startsWith(prefix)),
  };
}

export function memoryStore(): KvStore {
  const m = new Map<string, unknown>();
  return {
    get: async (k) => m.get(k),
    put: async (k, v) => void m.set(k, structuredClone(v)),
    remove: async (k) => void m.delete(k),
    keys: async (prefix) => [...m.keys()].filter((k) => k.startsWith(prefix)),
  };
}
